import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { getChannelHistory } from '../../api/system/system.api';
import HistoryMessageView from './HistoryMessageView';

const PAGE_SIZE = 50;

// 보기 필터. 값은 서버 `ChannelHistoryService.ActorType`·`Event` 이름과 같다.
// '메시지' = 운영 메시지 발송 전부(사건 하나 `MESSAGE_SENT`, 종류는 purpose 로 갈린다 — 2026-10-20).
const FILTERS = [
  { key: 'ALL', label: '전체', actorType: '', eventType: '' },
  { key: 'MESSAGE', label: '메시지', actorType: '', eventType: 'MESSAGE_SENT' },
  { key: 'ADMIN', label: '관리자', actorType: 'ADMIN', eventType: '' },
  { key: 'CUSTOMER', label: '고객', actorType: 'CUSTOMER', eventType: '' },
  { key: 'SYSTEM', label: '시스템', actorType: 'SYSTEM', eventType: '' },
];

const ACTOR_CLS = {
  ADMIN: 'bg-indigo-100 text-indigo-800',
  CUSTOMER: 'bg-green-100 text-green-800',
  SYSTEM: 'bg-gray-100 text-gray-700',
};

// payload 는 화면용으로 펼쳐 보이기만 한다. 서버가 전화번호·링크·토큰을 싣지 않는다(마스킹 값만).
const PayloadDetail = ({ payload }) => {
  const entries = Object.entries(payload ?? {}).filter(([k]) => k !== 'restored');
  if (entries.length === 0) return null;
  return (
    <table className="mt-1 rounded bg-gray-50 border border-gray-200 text-gray-600">
      <tbody>
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td className="px-2 py-0.5 font-mono text-gray-400 align-top whitespace-nowrap">{k}</td>
            <td className="px-2 py-0.5 font-mono break-all whitespace-pre-wrap">
              {v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v, null, 1) : String(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/**
 * '내용 보기'·'상세' 팝업(2026-10-23 — 종전엔 행 아래로 펼쳤다. 목록이 길어지고 어디를 스크롤할지 헷갈렸다).
 *
 * `document.body` 로 띄운다 — 고객사 상세 화면 안에 그리면 조상 요소의 overflow·transform 에 따라 `fixed` 가 잘릴 수 있다.
 * 닫기 = ✕ · 바깥(어두운 영역) 클릭 · Esc. 본문만 스크롤된다(스크롤바 보임).
 */
const HistoryModal = ({ title, subtitle, onClose, children }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose} data-testid="history-modal-backdrop">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 border-b flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-bold text-sm text-gray-800">{title}</div>
            {subtitle && <div className="mt-0.5 text-gray-500 break-all">{subtitle}</div>}
          </div>
          <button aria-label="닫기" className="text-gray-400 hover:text-gray-600 text-lg leading-none" onClick={onClose}>✕</button>
        </div>
        <div className="px-5 py-4 overflow-y-auto scrollbar-visible">{children}</div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * 고객사 **이력(타임라인)**(2026-10-19) — `channel_history` 를 최신순으로 보여준다.
 *
 * - 누가(관리자·고객·시스템) · 언제 · 무엇을. 사건 이름(`eventLabel`)은 서버가 정한다 — 화면에 표를 복제하지 않는다.
 * - '복원' 표시 = 과거 데이터(가입일·정산 확정일 등)에서 만든 행. **누가 했는지는 모른다.**
 * - 화면은 다시 정렬·판정하지 않는다(서버 순서 그대로). 필터는 보기만 좁힌다.
 *
 * 🔴 `key={channelId}` 로 붙인다 — 채널을 바꾸면 앞 채널 이력이 남지 않게 새로 만든다.
 * `version` 이 바뀌면 다시 읽는다(이 화면에서 서비스 중단·카드 링크 발송 등을 한 직후).
 */
const ChannelHistoryTimeline = ({ channelId, version }) => {
  const [filterKey, setFilterKey] = useState('ALL');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // 팝업 하나 — { type: 'message' | 'detail', row }. '내용 보기' 는 열 때만 서버에서 읽는다(HistoryMessageView 가 열릴 때 조회).
  const [modal, setModal] = useState(null);
  const closeModal = useCallback(() => setModal(null), []);
  const filter = FILTERS.find((f) => f.key === filterKey) ?? FILTERS[0];

  const load = useCallback(async (nextPage, append) => {
    setLoading(true);
    setErr('');
    try {
      const res = await getChannelHistory(channelId, {
        page: nextPage, size: PAGE_SIZE, actorType: filter.actorType, eventType: filter.eventType,
      });
      const data = res?.data;
      setRows((prev) => (append ? [...prev, ...(data?.rows ?? [])] : data?.rows ?? []));
      setTotal(data?.total ?? 0);
      setPage(nextPage);
    } catch (e) {
      // 실패를 '이력 없음' 으로 그리지 않는다 — 감사 화면이 비어 보이면 "아무 일도 없었다" 로 읽힌다.
      setErr(e?.message ?? '이력을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [channelId, filter.actorType, filter.eventType]);

  useEffect(() => { load(0, false); }, [load, version]);

  return (
    <div className="text-xs">
      <div className="flex flex-wrap items-center gap-1 mb-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilterKey(f.key)}
            className={`px-2 py-0.5 rounded border ${filterKey === f.key
              ? 'bg-gray-800 text-white border-gray-800'
              : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
          >
            {f.label}
          </button>
        ))}
        <span className="ml-2 text-gray-400">총 {total.toLocaleString('ko-KR')}건</span>
        <button onClick={() => load(0, false)} disabled={loading} className="ml-auto px-2 py-0.5 rounded border bg-white border-gray-300 text-gray-600 hover:bg-gray-50">
          새로고침
        </button>
      </div>

      {err && (
        <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-700">
          이력을 불러오지 못했습니다: {err}
        </div>
      )}

      {!err && rows.length === 0 && !loading && (
        <div className="py-3 text-center text-gray-400">기록된 이력이 없습니다.</div>
      )}

      {rows.length > 0 && (
        // `scrollbar-visible` 필수(2026-10-22) — common.css 가 앱 전역에서 스크롤바를 숨겨서, 상세를 펼쳐 목록이
        // 길어져도 스크롤바가 0px 이라 '상세 안을 스크롤해야 하나' 헷갈린다(로컬 테스트 피드백). AllCustomersAdmin 과 같은 처리.
        <ol className="max-h-96 overflow-y-auto divide-y border rounded scrollbar-visible">
          {rows.map((r) => (
            <li key={r.id} className="px-3 py-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-gray-500 whitespace-nowrap">{r.occurredAt}</span>
                <span className={`px-1.5 py-0.5 rounded ${ACTOR_CLS[r.actorType] ?? 'bg-gray-100 text-gray-700'}`}>
                  {r.actorLabel}{r.actorId && !r.restored ? ` · ${r.actorId}` : ''}
                </span>
                <b className="text-gray-800">{r.eventLabel}</b>
                {r.restored && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-50 text-yellow-700 border border-yellow-200" title="과거 데이터에서 복원한 기록입니다. 누가 했는지는 알 수 없습니다.">복원</span>
                )}
                {r.hasMessage && (
                  <button onClick={() => setModal({ type: 'message', row: r })} className="text-indigo-600 underline">
                    내용 보기
                  </button>
                )}
                {r.payload && Object.keys(r.payload).some((k) => k !== 'restored') && (
                  <button onClick={() => setModal({ type: 'detail', row: r })} className="text-gray-400 underline">
                    상세
                  </button>
                )}
              </div>
              <div className="mt-0.5 text-gray-600">{r.summary}</div>
            </li>
          ))}
        </ol>
      )}

      {modal && (
        <HistoryModal
          title={`${modal.row.eventLabel} · ${modal.type === 'message' ? '보낸 내용' : '상세'}`}
          subtitle={`${modal.row.occurredAt} · ${modal.row.actorLabel}${modal.row.actorId && !modal.row.restored ? ` · ${modal.row.actorId}` : ''} — ${modal.row.summary}`}
          onClose={closeModal}
        >
          {modal.type === 'message'
            ? <HistoryMessageView key={modal.row.id} channelId={channelId} historyId={modal.row.id} />
            : <PayloadDetail payload={modal.row.payload} />}
        </HistoryModal>
      )}

      {rows.length < total && (
        <button onClick={() => load(page + 1, true)} disabled={loading} className="mt-2 w-full py-1 rounded border bg-white border-gray-300 text-gray-600 hover:bg-gray-50">
          {loading ? '불러오는 중…' : `더 보기 (${rows.length.toLocaleString('ko-KR')} / ${total.toLocaleString('ko-KR')})`}
        </button>
      )}
    </div>
  );
};

export default ChannelHistoryTimeline;
